import { Buffer } from 'buffer';
import matter from 'gray-matter';
import type { StudioModule, StudioGenerateResponse } from '../types/studio';
import type { StudioGenerateRequest } from '../types/studio';

interface SerializeStudioParams {
  studio: StudioModule | undefined;
  response: StudioGenerateResponse;
  request: StudioGenerateRequest;
}

if (typeof globalThis !== 'undefined' && (globalThis as any).Buffer === undefined) {
  (globalThis as any).Buffer = Buffer;
}

const _sanitizeArray = (value: any[]): any => {
  const sanitizedArray = value
    .map((item) => sanitizeMetadata(item))
    .filter((item) => item !== undefined);
  return sanitizedArray.length > 0 ? sanitizedArray : undefined;
};

const _sanitizeObject = (value: Record<string, any>): any => {
  const sanitizedObject: Record<string, any> = {};
  Object.entries(value).forEach(([key, val]) => {
    const sanitizedVal = sanitizeMetadata(val);
    if (sanitizedVal !== undefined) {
      sanitizedObject[key] = sanitizedVal;
    }
  });
  return Object.keys(sanitizedObject).length > 0 ? sanitizedObject : undefined;
};

const sanitizeMetadata = (value: any): any => {
  if (value === undefined || value === null) {
    return value;
  }

  if (Array.isArray(value)) {
    return _sanitizeArray(value);
  }

  if (typeof value === 'object') {
    return _sanitizeObject(value);
  }

  return value;
};

const OMIT_KEYS = new Set([
  'citations',
  'chunks',
  'chunk_texts',
  'selected_chunks',
  'document_chunks',
  'metadata',
  'raw_response',
  'response',
  'raw',
  'sources',
]);

const isNonEmptyString = (value: unknown): value is string =>
  typeof value === 'string' && value.trim().length > 0;

const toTitleCase = (value: string) =>
  value
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^[a-z]/, (match) => match.toUpperCase());

const formatList = (items: unknown[]): string => {
  const lines = items
    .map((item) => {
      if (isNonEmptyString(item)) {
        return `- ${item.trim()}`;
      }
      if (item && typeof item === 'object') {
        return `- ${formatObject(item as Record<string, any>)}`;
      }
      return `- ${String(item)}`;
    })
    .filter(Boolean);

  return lines.join('\n');
};

const _shouldOmitKey = (key: string, val: any): boolean => {
  if (OMIT_KEYS.has(key)) return true;
  if (Array.isArray(val) && val.length === 0) return true;
  if (val && typeof val === 'object' && Object.keys(val).length === 0) return true;
  return false;
};

const _formatObjectValue = (title: string, val: any): string | null => {
  if (isNonEmptyString(val)) {
    return `${title}\n${val.trim()}`;
  }
  if (Array.isArray(val)) {
    return `${title}\n${formatList(val)}`;
  }
  if (val && typeof val === 'object') {
    return `${title}\n${formatObject(val)}`;
  }
  return null;
};

const formatObject = (value: Record<string, any>): string => {
  const sections: string[] = [];

  Object.entries(value).forEach(([key, val]) => {
    if (_shouldOmitKey(key, val)) return;

    const title = `**${toTitleCase(key)}**`;
    const formatted = _formatObjectValue(title, val);
    if (formatted) {
      sections.push(formatted);
    }
  });

  return sections.join('\n\n').trim();
};

const _addSummaryTitle = (sections: string[], result: Record<string, any>): void => {
  if (isNonEmptyString(result.titulo)) {
    sections.push(`### ${result.titulo.trim()}`);
  }
};

const _addSummarySections = (sections: string[], result: Record<string, any>): void => {
  if (Array.isArray(result.puntos_clave) && result.puntos_clave.length > 0) {
    sections.push(`**Puntos clave**\n${formatList(result.puntos_clave)}`);
  }
  if (Array.isArray(result.insights_principales) && result.insights_principales.length > 0) {
    sections.push(`**Insights principales**\n${formatList(result.insights_principales)}`);
  }
  if (result.secciones && typeof result.secciones === 'object') {
    sections.push(`**Secciones**\n${formatObject(result.secciones)}`);
  }
  if (Array.isArray(result.conclusiones) && result.conclusiones.length > 0) {
    sections.push(`**Conclusiones**\n${formatList(result.conclusiones)}`);
  }
  if (Array.isArray(result.recomendaciones) && result.recomendaciones.length > 0) {
    sections.push(`**Recomendaciones**\n${formatList(result.recomendaciones)}`);
  }
};

const formatSummaryResult = (result: Record<string, any>): string | undefined => {
  const sections: string[] = [];
  _addSummaryTitle(sections, result);
  _addSummarySections(sections, result);

  if (sections.length === 0 && isNonEmptyString(result.contenido)) {
    sections.push(result.contenido.trim());
  }

  return sections.length > 0 ? sections.join('\n\n') : undefined;
};

const formatTimelineResult = (result: Record<string, any>): string | undefined => {
  if (!Array.isArray(result.events) || result.events.length === 0) {
    return undefined;
  }

  const lines = result.events
    .map((event: any) => {
      const date = isNonEmptyString(event.fecha) ? `**${event.fecha.trim()}**` : '';
      const title = isNonEmptyString(event.titulo) ? ` ${event.titulo.trim()}` : '';
      const description = isNonEmptyString(event.descripcion) ? `\n${event.descripcion.trim()}` : '';
      const category = isNonEmptyString(event.categoria) ? ` _(Categoría: ${event.categoria.trim()})_` : '';

      return `- ${date}${title}${category}${description}`;
    })
    .filter(Boolean);

  return lines.length > 0 ? lines.join('\n') : undefined;
};

const formatQAResult = (result: Record<string, any>): string | undefined => {
  if (!Array.isArray(result.qa_pairs) || result.qa_pairs.length === 0) {
    return undefined;
  }

  const pairs = result.qa_pairs
    .map((pair: any, index: number) => {
      const question = isNonEmptyString(pair.pregunta)
        ? pair.pregunta.trim()
        : isNonEmptyString(pair.question)
          ? pair.question.trim()
          : `Pregunta ${index + 1}`;
      const answer = isNonEmptyString(pair.respuesta)
        ? pair.respuesta.trim()
        : isNonEmptyString(pair.answer)
          ? pair.answer.trim()
          : '';
      if (!answer) {
        return null;
      }
      return `**Q:** ${question}\n**A:** ${answer}`;
    })
    .filter(Boolean);

  return pairs.length > 0 ? pairs.join('\n\n') : undefined;
};

const formatGenericResult = (result: Record<string, any>): string | undefined => {
  if (isNonEmptyString(result.contenido)) {
    return result.contenido.trim();
  }
  if (isNonEmptyString(result.descripcion)) {
    return result.descripcion.trim();
  }
  if (isNonEmptyString(result.summary)) {
    return result.summary.trim();
  }
  return undefined;
};

export const formatAnswerMarkdown = (result: any, studio?: StudioModule): string => {
  if (!result) {
    return 'Sin contenido disponible.';
  }

  if (isNonEmptyString(result)) {
    return result.trim();
  }

  if (Array.isArray(result)) {
    return formatList(result);
  }

  if (result && typeof result === 'object') {
    const summary = formatSummaryResult(result);
    if (summary) return summary;

    const qa = formatQAResult(result);
    if (qa) return qa;

    const timeline = formatTimelineResult(result);
    if (timeline) return timeline;

    const generic = formatGenericResult(result);
    if (generic) return generic;

    return formatObject(result);
  }

  return String(result);
};

export const extractQuestion = (params: {
  studio?: StudioModule;
  response: StudioGenerateResponse;
  result: any;
  request: StudioGenerateRequest;
  studioMeta?: Record<string, any>;
}): string | undefined => {
  const { studio, response, result, request, studioMeta } = params;
  const metadata = (response.metadata || {}) as Record<string, any>;
  const resultMetadata =
    result && typeof result === 'object'
      ? ((result.metadata || (result as Record<string, any>)) as Record<string, any>)
      : undefined;

  const candidates = [
    studioMeta?.rag_query,
    metadata.source_query,
    metadata.query,
    resultMetadata?.source_query,
    resultMetadata?.query,
    result?.query,
    metadata.summary,
    studioMeta?.summary,
    studio?.description,
    studio?.name ? `Generación: ${studio.name}` : undefined,
    request.document_ids && request.document_ids.length > 0
      ? `Análisis de ${request.document_ids.length} documento(s)`
      : undefined,
  ];

  for (const value of candidates) {
    if (isNonEmptyString(value)) {
      return value.trim();
    }
  }

  return undefined;
};

const _extractAttachments = (assistantMessage: any): Record<string, any> | undefined => {
  if (assistantMessage && typeof assistantMessage === 'object' && assistantMessage.attachments && typeof assistantMessage.attachments === 'object') {
    return assistantMessage.attachments as Record<string, any>;
  }
  return undefined;
};

const _extractStudioAttachment = (attachments: Record<string, any> | undefined): Record<string, any> | undefined => {
  if (attachments?.studio_metadata && typeof attachments.studio_metadata === 'object') {
    return { ...(attachments.studio_metadata as Record<string, any>) };
  }
  return undefined;
};

const _buildBaseMetadata = (studio: StudioModule | undefined, request: StudioGenerateRequest, response: StudioGenerateResponse, processingTimeMs: number | undefined, assistantMessage: any, restStudioMeta: Record<string, any>): Record<string, any> => {
  return {
    studio_id: studio?.id,
    studio_name: studio?.name,
    studio_type: studio?.generator_key,
    document_ids: request.document_ids,
    generated_at: response.generated_at,
    cached: response.cached,
    processing_time_ms: processingTimeMs,
    usage_stats: response.usage_stats ?? assistantMessage?.usage_stats,
    rag_stats: response.rag_stats ?? restStudioMeta?.rag_stats,
  };
};

const _mergeResponseMetadata = (metadata: Record<string, any>, responseMetadata: Record<string, any>): void => {
  Object.entries(responseMetadata).forEach(([key, value]) => {
    if (metadata[key] === undefined) {
      metadata[key] = value;
    }
  });
};

const _mergeStudioMetadata = (metadata: Record<string, any>, studioMeta: Record<string, any>): void => {
  Object.entries(studioMeta).forEach(([key, value]) => {
    if (metadata[key] === undefined) {
      metadata[key] = value;
    }
  });
};

const _buildContentSections = (question: string | undefined, answerMarkdown: string): string => {
  const sections: string[] = [];
  if (question) {
    sections.push(`**Pregunta:** ${question}`);
  }
  if (answerMarkdown.trim().length > 0) {
    sections.push(`**Respuesta:**\n\n${answerMarkdown.trim()}`);
  } else {
    sections.push('**Respuesta:** Sin contenido disponible.');
  }
  return sections.join('\n\n');
};

export const serializeStudioResultToMarkdown = ({
  studio,
  response,
  request
}: SerializeStudioParams) => {
  const assistantMessage = response.assistant_message;
  const attachments = _extractAttachments(assistantMessage);
  const studioAttachment = _extractStudioAttachment(attachments);

  const { result_data: attachmentResultData, rag_result: attachmentRagResult, ...restStudioMeta } = studioAttachment || {};

  const processingTimeMs = response.processing_time_ms ?? (typeof response.processing_time === 'number' ? Math.round(response.processing_time * 1000) : undefined);

  const metadata = _buildBaseMetadata(studio, request, response, processingTimeMs, assistantMessage, restStudioMeta);

  const usageStats = metadata.usage_stats;
  if (metadata.tokens_used === undefined && usageStats && typeof usageStats === 'object' && typeof (usageStats as any).total_tokens === 'number') {
    metadata.tokens_used = (usageStats as any).total_tokens;
  }

  if (response.metadata && typeof response.metadata === 'object') {
    _mergeResponseMetadata(metadata, response.metadata);
  }

  if (restStudioMeta && typeof restStudioMeta === 'object') {
    _mergeStudioMetadata(metadata, restStudioMeta);
  }

  if (!metadata.title) {
    metadata.title = studio?.name || metadata.studio_name || 'Resultado Studio';
  }

  const rawResult = attachmentResultData ?? (response.metadata && (response.metadata as any).result_data) ?? null;
  const baseResult = rawResult ?? assistantMessage?.content ?? attachmentRagResult?.response ?? '';

  const sanitizedMetadata = sanitizeMetadata(metadata) ?? {};
  const question = extractQuestion({ studio, response, result: baseResult, request, studioMeta: restStudioMeta });
  const answerMarkdown = formatAnswerMarkdown(baseResult || assistantMessage?.content || '', studio);
  const content = _buildContentSections(question, answerMarkdown);

  return {
    markdown: matter.stringify(content, sanitizedMetadata),
    metadata: sanitizedMetadata,
    rawResult: rawResult ?? assistantMessage?.content ?? baseResult,
    assistantMessage,
  };
};

type MarkdownInput = string | { markdown?: string } | null | undefined;

export const parseStudioMarkdown = (input?: MarkdownInput) => {
  try {
    let markdown = '';

    if (typeof input === 'string') {
      markdown = input;
    } else if (input && typeof input === 'object' && typeof input.markdown === 'string') {
      markdown = input.markdown;
    }

    return matter(markdown);
  } catch (error) {
    console.error('Error parsing studio markdown:', error);
    return matter('');
  }
};
